import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { getContext, getMasterGain } from "@/lib/audioEngine";
import BinauralHUD from "@/components/BinauralHUD";

/* ─── BRAINWAVE PRESETS ─── */
const BRAINWAVE_PRESETS = [
  { id: "delta", label: "DELTA", hz: 2, desc: "Deep sleep · 0.5-4 Hz" },
  { id: "theta", label: "THETA", hz: 6, desc: "Meditation · 4-8 Hz" },
  { id: "alpha", label: "ALPHA", hz: 10, desc: "Relaxed focus · 8-13 Hz" },
  { id: "beta", label: "BETA", hz: 20, desc: "Active thinking · 13-30 Hz" },
  { id: "gamma", label: "GAMMA", hz: 40, desc: "Insight · 30-40 Hz" },
];

/* ─── NOISE FOR CORTICAL FOLDS ─── */
function cNoise(x, y, z) {
  const n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
  return (n - Math.floor(n)) * 2 - 1;
}
function folds(x, y, z) {
  return cNoise(x * 3, y * 3, z * 3) * 0.06
    + cNoise(x * 7, y * 7, z * 7) * 0.03
    + cNoise(x * 15, y * 15, z * 15) * 0.015;
}

/* ─── THREE.JS BRAIN MESH ─── */
function BrainMesh({ activeFreq, beatHz, energy, sessionOn }) {
  const pinealRef = useRef();
  const timeRef = useRef(0);

  const { geo, regionMap } = useMemo(() => {
    const g = new THREE.SphereGeometry(1, 56, 40);
    const pos = g.attributes.position;
    const count = pos.count;
    const colors = new Float32Array(count * 3);
    const rMap = new Array(count);

    for (let i = 0; i < count; i++) {
      let x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const len = Math.sqrt(x * x + y * y + z * z);
      if (len === 0) continue;
      x /= len; y /= len; z /= len;

      // Brain proportions
      let bx = x * 0.75, by = y * 0.9, bz = z * 0.85;

      // Frontal lobe bulge
      if (bz > 0) { bx *= 1 + bz * 0.12; bz *= 1.05; }
      // Temporal lobe
      if (Math.abs(bx) > 0.3 && by < 0.1) bx *= 1.08;
      // Longitudinal fissure
      if (Math.abs(bx) < 0.08) by -= (1 - Math.abs(bx) / 0.08) * 0.05;
      // Cortical folds
      const f = folds(bx * 5, by * 5, bz * 5);
      bx += (x / len) * f;
      by += (y / len) * f;
      bz += (z / len) * f;

      pos.setXYZ(i, bx, by, bz);

      // Region classification
      if (by > 0.5) rMap[i] = "crown";
      else if (bz > 0.3) rMap[i] = "frontal";
      else if (Math.abs(bx) > 0.4) rMap[i] = "temporal";
      else if (bz < -0.2) rMap[i] = "occipital";
      else rMap[i] = "parietal";

      colors[i * 3] = 0.12; colors[i * 3 + 1] = 0; colors[i * 3 + 2] = 0;
    }
    g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    g.computeVertexNormals();
    return { geo: g, regionMap: rMap };
  }, []);

  useFrame((_, delta) => {
    timeRef.current += delta;
    const t = timeRef.current;
    const ca = geo.attributes.color;

    for (let i = 0; i < regionMap.length; i++) {
      const base = sessionOn ? 0.12 + energy * 0.5 : 0.08;
      const beatPulse = sessionOn ? Math.sin(t * beatHz * Math.PI * 2 + i * 0.02) * 0.12 : 0;
      const freqWave = sessionOn ? Math.sin(t * 2 + (activeFreq / 200) + i * 0.005) * 0.06 : 0;

      let r = base + beatPulse + freqWave;
      let g = 0, b = 0;

      // Temporal lobes shimmer with binaural beat
      if (regionMap[i] === "temporal") {
        const binShimmer = sessionOn ? Math.abs(Math.sin(t * beatHz * Math.PI + i * 0.15)) * 0.08 : 0;
        r += binShimmer;
        b = binShimmer * 0.3;
      }
      // Crown activation at high frequencies
      if (regionMap[i] === "crown" && activeFreq > 800) {
        r += 0.1; g += 0.02;
      }
      ca.setXYZ(i, Math.min(r, 1), Math.min(g, 0.05), Math.min(b, 0.1));
    }
    ca.needsUpdate = true;

    // Pineal gland pulse
    if (pinealRef.current) {
      const pi = sessionOn
        ? 0.8 + Math.sin(t * beatHz * Math.PI * 2) * 0.5 + energy * 0.5
        : 0.15 + Math.sin(t * 0.5) * 0.05;
      pinealRef.current.material.emissiveIntensity = pi;
      const s = 1 + (sessionOn ? Math.sin(t * beatHz * Math.PI * 2) * 0.2 + energy * 0.15 : 0);
      pinealRef.current.scale.setScalar(s);
    }
  });

  return (
    <group>
      <mesh geometry={geo}>
        <meshBasicMaterial wireframe transparent opacity={0.08} color="#FF0000" />
      </mesh>
      <points geometry={geo}>
        <pointsMaterial size={0.018} vertexColors transparent opacity={0.85} sizeAttenuation />
      </points>
      {/* Pineal gland — center of brain, slightly posterior */}
      <mesh ref={pinealRef} position={[0, -0.05, -0.08]}>
        <sphereGeometry args={[0.055, 16, 16]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#FF0000"
          emissiveIntensity={0.5}
          transparent
          opacity={0.95}
        />
      </mesh>
      {/* Pineal glow halo */}
      <mesh position={[0, -0.05, -0.08]}>
        <sphereGeometry args={[0.09, 12, 12]} />
        <meshBasicMaterial color="#FF0000" transparent opacity={sessionOn ? 0.12 : 0.03} />
      </mesh>
    </group>
  );
}

/* ─── MAIN COMPONENT ─── */
export default function BinauralBrain() {
  const [baseFreq, setBaseFreq] = useState(432);
  const [beatHz, setBeatHz] = useState(10);
  const [activePreset, setActivePreset] = useState("alpha");
  const [sessionOn, setSessionOn] = useState(false);
  const [energy, setEnergy] = useState(0);
  const nodesRef = useRef({ left: null, right: null, leftPanner: null, rightPanner: null, gain: null });
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const startSession = useCallback(() => {
    const ctx = getContext();
    if (ctx.state === "suspended") ctx.resume();
    const master = getMasterGain();

    // Left ear — base frequency
    const leftOsc = ctx.createOscillator();
    leftOsc.type = "sine";
    leftOsc.frequency.value = baseFreq;
    const leftGain = ctx.createGain();
    leftGain.gain.value = 0.0001;
    const leftPanner = ctx.createPanner();
    leftPanner.panningModel = "HRTF";
    leftPanner.distanceModel = "inverse";
    leftPanner.refDistance = 1;
    leftPanner.positionX.value = -1.5;
    leftPanner.positionY.value = 0;
    leftPanner.positionZ.value = 0;
    leftOsc.connect(leftGain);
    leftGain.connect(leftPanner);
    leftPanner.connect(master);

    // Right ear — base + beat Hz
    const rightOsc = ctx.createOscillator();
    rightOsc.type = "sine";
    rightOsc.frequency.value = baseFreq + beatHz;
    const rightGain = ctx.createGain();
    rightGain.gain.value = 0.0001;
    const rightPanner = ctx.createPanner();
    rightPanner.panningModel = "HRTF";
    rightPanner.distanceModel = "inverse";
    rightPanner.refDistance = 1;
    rightPanner.positionX.value = 1.5;
    rightPanner.positionY.value = 0;
    rightPanner.positionZ.value = 0;
    rightOsc.connect(rightGain);
    rightGain.connect(rightPanner);
    rightPanner.connect(master);

    // Harmonic — perfect fifth, centered above
    const harmOsc = ctx.createOscillator();
    harmOsc.type = "sine";
    harmOsc.frequency.value = baseFreq * 1.5;
    const harmGain = ctx.createGain();
    harmGain.gain.value = 0.0001;
    const harmPanner = ctx.createPanner();
    harmPanner.panningModel = "HRTF";
    harmPanner.positionX.value = 0;
    harmPanner.positionY.value = 1.5;
    harmPanner.positionZ.value = -0.5;
    harmOsc.connect(harmGain);
    harmGain.connect(harmPanner);
    harmPanner.connect(master);

    // Sub-bass — below listener
    const subOsc = ctx.createOscillator();
    subOsc.type = "sine";
    subOsc.frequency.value = baseFreq / 4;
    const subGain = ctx.createGain();
    subGain.gain.value = 0.0001;
    const subPanner = ctx.createPanner();
    subPanner.panningModel = "HRTF";
    subPanner.positionX.value = 0;
    subPanner.positionY.value = -1;
    subPanner.positionZ.value = -1;
    subOsc.connect(subGain);
    subGain.connect(subPanner);
    subPanner.connect(master);

    // Analyser for energy readout
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    leftGain.connect(analyser);
    analyserRef.current = analyser;

    // Start all
    leftOsc.start(); rightOsc.start(); harmOsc.start(); subOsc.start();

    // Fade in
    const now = ctx.currentTime;
    leftGain.gain.setTargetAtTime(0.12, now, 0.08);
    rightGain.gain.setTargetAtTime(0.12, now, 0.08);
    harmGain.gain.setTargetAtTime(0.06, now, 0.08);
    subGain.gain.setTargetAtTime(0.04, now, 0.08);

    nodesRef.current = {
      left: leftOsc, right: rightOsc, harm: harmOsc, sub: subOsc,
      leftGain, rightGain, harmGain, subGain,
      leftPanner, rightPanner, harmPanner, subPanner,
    };
    setSessionOn(true);

    // Energy monitor loop
    const dataArr = new Uint8Array(analyser.frequencyBinCount);
    const monitor = () => {
      analyser.getByteFrequencyData(dataArr);
      let sum = 0;
      for (let i = 0; i < dataArr.length; i++) sum += dataArr[i];
      setEnergy(Math.min((sum / dataArr.length) / 128, 1));
      rafRef.current = requestAnimationFrame(monitor);
    };
    monitor();
  }, [baseFreq, beatHz]);

  const stopSession = useCallback(() => {
    const n = nodesRef.current;
    const ctx = getContext();
    const now = ctx.currentTime;
    // Fade out all gains
    [n.leftGain, n.rightGain, n.harmGain, n.subGain].forEach(g => {
      if (g) g.gain.setTargetAtTime(0.0001, now, 0.06);
    });
    setTimeout(() => {
      [n.left, n.right, n.harm, n.sub].forEach(o => { try { o?.stop(); } catch {} });
      nodesRef.current = {};
    }, 200);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setSessionOn(false);
    setEnergy(0);
  }, []);

  // Live frequency glide when user adjusts sliders mid-session
  useEffect(() => {
    if (!sessionOn) return;
    const n = nodesRef.current;
    const ctx = getContext();
    if (n.left) n.left.frequency.setTargetAtTime(baseFreq, ctx.currentTime, 0.05);
    if (n.right) n.right.frequency.setTargetAtTime(baseFreq + beatHz, ctx.currentTime, 0.05);
    if (n.harm) n.harm.frequency.setTargetAtTime(baseFreq * 1.5, ctx.currentTime, 0.05);
    if (n.sub) n.sub.frequency.setTargetAtTime(baseFreq / 4, ctx.currentTime, 0.05);
  }, [baseFreq, beatHz, sessionOn]);

  // Cleanup
  useEffect(() => () => { if (sessionOn) stopSession(); }, []);

  const selectPreset = (p) => {
    setActivePreset(p.id);
    setBeatHz(p.hz);
  };

  return (
    <div className="space-y-4" data-testid="binaural-brain-page">
      {/* 3D Brain */}
      <Card className="sg-frame border-[#FF0000]/20" data-testid="binaural-brain">
        <CardHeader className="pb-2">
          <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono" data-testid="binaural-brain-title">
            &gt; BI-NUARAL CORTEX
          </CardTitle>
          <p className="text-[10px] tracking-widest uppercase text-[#666]">
            HRTF Dolby Atmos binaural rendering · Pineal gland frequency response · Use headphones
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="border border-[#FF0000]/15 bg-black overflow-hidden relative" style={{ height: "380px" }}>
            <Canvas camera={{ position: [0, 0.3, 2], fov: 45 }} dpr={[1, 1.5]}>
              <color attach="background" args={["#000000"]} />
              <ambientLight intensity={0.03} />
              <pointLight position={[2, 2, 2]} intensity={0.25} color="#FF0000" />
              <pointLight position={[-2, -1, 1]} intensity={0.12} color="#FF3333" />
              <BrainMesh activeFreq={baseFreq} beatHz={beatHz} energy={energy} sessionOn={sessionOn} />
              <OrbitControls enablePan={false} minDistance={1.2} maxDistance={3.5} autoRotate autoRotateSpeed={0.3} />
            </Canvas>
            {/* Frequency-Map style HUD overlay */}
            <BinauralHUD
              activeFreq={baseFreq}
              beatHz={beatHz}
              energy={energy}
              sessionOn={sessionOn}
              presetLabel={(BRAINWAVE_PRESETS.find(p => p.id === activePreset)?.label) || "—"}
            />
          </div>
          {/* Pineal status */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${sessionOn ? "animate-pulse" : ""}`}
                style={{ backgroundColor: sessionOn ? "#FF0000" : "#330000" }} />
              <span className="text-[9px] font-mono tracking-widest text-[#FF0000]/70">
                PINEAL GLAND {sessionOn ? "· RESONATING" : "· STANDBY"}
              </span>
            </div>
            <span className="text-[9px] font-mono tracking-widest text-[#555]">
              {sessionOn ? `${baseFreq} Hz · ${beatHz} Hz beat · HRTF SPATIAL` : "Drag to rotate · Start session to activate"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="sg-frame border-[#FF0000]/20" data-testid="binaural-controls">
        <CardHeader className="pb-2">
          <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono">
            &gt; FREQUENCY CONTROL
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Brainwave presets */}
          <div>
            <div className="text-[9px] font-mono tracking-widest text-[#666] mb-2">BRAINWAVE TARGET</div>
            <div className="flex flex-wrap gap-1.5" data-testid="brainwave-presets">
              {BRAINWAVE_PRESETS.map(p => (
                <button
                  key={p.id}
                  onClick={() => selectPreset(p)}
                  className={`px-3 py-1.5 text-[9px] tracking-widest uppercase font-mono border transition-all ${
                    activePreset === p.id
                      ? "border-[#FF0000]/60 bg-[#FF0000]/15 text-[#FF0000] shadow-[0_0_8px_rgba(255,0,0,0.15)]"
                      : "border-[#FF0000]/20 text-[#555] hover:text-[#FF0000]"
                  }`}
                  data-testid={`preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="text-[8px] font-mono text-[#555] mt-1">
              {BRAINWAVE_PRESETS.find(p => p.id === activePreset)?.desc}
            </div>
          </div>

          {/* Base frequency */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-[9px] font-mono tracking-widest text-[#666]">BASE FREQUENCY</span>
              <span className="text-[9px] font-mono text-[#FF0000]">{baseFreq} Hz</span>
            </div>
            <Slider
              value={[baseFreq]}
              onValueChange={v => setBaseFreq(v[0])}
              min={100} max={963} step={1}
              className="my-1"
              data-testid="freq-slider"
            />
            <div className="flex justify-between text-[8px] font-mono text-[#444]">
              <span>100</span><span>432</span><span>528</span><span>963</span>
            </div>
          </div>

          {/* Beat frequency */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-[9px] font-mono tracking-widest text-[#666]">BINAURAL BEAT</span>
              <span className="text-[9px] font-mono text-[#FF0000]">{beatHz.toFixed(1)} Hz</span>
            </div>
            <Slider
              value={[beatHz]}
              onValueChange={v => setBeatHz(v[0])}
              min={0.5} max={40} step={0.5}
              className="my-1"
              data-testid="beat-slider"
            />
            <div className="flex justify-between text-[8px] font-mono text-[#444]">
              <span>Delta 0.5</span><span>Theta 6</span><span>Alpha 10</span><span>Beta 20</span><span>Gamma 40</span>
            </div>
          </div>

          {/* Session button */}
          <button
            onClick={sessionOn ? stopSession : startSession}
            className={`w-full py-3 text-xs tracking-widest uppercase font-mono border transition-all ${
              sessionOn
                ? "border-[#FF0000] bg-[#FF0000]/20 text-[#FF0000] shadow-[0_0_20px_rgba(255,0,0,0.2)]"
                : "border-[#FF0000]/40 text-[#FF0000]/70 hover:bg-[#FF0000]/10 hover:text-[#FF0000]"
            }`}
            data-testid="session-toggle"
          >
            {sessionOn ? "STOP BINAURAL SESSION" : "START HRTF BINAURAL SESSION"}
          </button>

          {sessionOn && (
            <div className="flex items-center gap-3 text-[9px] font-mono text-[#FF0000]/60">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse" />
                LIVE
              </div>
              <span>L: {baseFreq} Hz · R: {baseFreq + beatHz} Hz · Harmonic: {Math.round(baseFreq * 1.5)} Hz · Sub: {Math.round(baseFreq / 4)} Hz</span>
            </div>
          )}

          <div className="text-[8px] font-mono text-[#444] tracking-wider">
            4 HRTF-spatialized oscillators · Left/Right ear binaural · Overhead harmonic · Sub-bass below · Headphones required for full Dolby Atmos effect
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
